import { useEffect, useMemo, useRef } from 'react';

import { useAppState } from '../../../AppStateProvider';
import { Page } from '../../../domain/page';
import { USER_SAM_ON_BOARDING_COMPLETED } from '../../../domain/user';
import { useCompaniesQuery } from '../../../features/company/useCompaniesQuery';
import { useCompanyQuery } from '../../../features/company/useCompanyQuery';
import { useUserQuery } from '../../../features/user/useUserQuery';
import { useIsSwUser } from '../../../hooks/useIsSwUser';
import { useIsWithinCrm } from '../../../hooks/useIsWithinCrm';
import { useIsWithinTools } from '../../../hooks/useIsWithinTools';
import { usePrevious } from '../../../hooks/usePrevious';
import DataExtractionService from '../../dataExtraction/DataExtractionService';
import UrlService from '../../UrlService';
import type { TrackingConfigItem } from '../trackingConfig';
import { pagesTrackingConfig, tabsTrackingConfig } from '../trackingConfig';
import TrackingPageViewService from '../TrackingPageViewService';

export const usePageViewTracking = () => {
  const {
    page,
    isOpen,
    url = '',
    companyTabName,
    collectedMeeting,
    companyDomain,
    trackingSectionName,
  } = useAppState();
  const collectedMeetingDomains = collectedMeeting?.domains || [];
  const isSwUser = useIsSwUser();
  const { data: user, isLoading: isUserLoading } = useUserQuery();
  const userDomain = DataExtractionService.meeting.extractDomainFromEmail(user?.email || '');
  const isSAMOnboardingCompleted = Boolean(user?.userSettings?.[USER_SAM_ON_BOARDING_COMPLETED]);

  const { isWithinTools } = useIsWithinTools();
  const isWithinCrm = useIsWithinCrm();

  const urlObject = UrlService.createEventUrl(url);

  const prevUrl = usePrevious(url);
  const prevCompanyTabName = usePrevious(companyTabName);
  const prevIsSAMOnboardingCompleted = usePrevious(isSAMOnboardingCompleted);

  const lastLoggedToolsDomain = useRef<string | undefined>();

  const companiesQuery = useCompaniesQuery();
  const companyQuery = useCompanyQuery();

  const config = useMemo(() => {
    let config: TrackingConfigItem | undefined = pagesTrackingConfig.find((config) => config.trackingId === page);
    if (
      (!!prevCompanyTabName && prevCompanyTabName !== companyTabName) ||
      !config ||
      prevIsSAMOnboardingCompleted !== isSAMOnboardingCompleted
    ) {
      const tabConfig = tabsTrackingConfig.find((config) => config.trackingId === companyTabName);
      if (typeof tabConfig?.subSubSection === 'function') {
        config = {
          ...tabConfig,
          subSubSection: tabConfig.subSubSection(page, isSAMOnboardingCompleted),
        } as TrackingConfigItem;
      } else {
        config = tabConfig as TrackingConfigItem;
      }
    }
    return config;
  }, [page, companyTabName, isSAMOnboardingCompleted]);

  const trackPageView = () => {
    if (isUserLoading) {
      return;
    }
    const section = trackingSectionName || 'si extension';
    if (prevUrl !== url) {
      if (UrlService.isLinkedinUrl(urlObject)) {
        TrackingPageViewService.trackPageView({ config, url, page, isSwUser, section });
        return;
      }
      if (prevUrl && new URL(prevUrl).hostname === urlObject?.hostname) {
        return;
      }
    }
    TrackingPageViewService.trackPageView({ config, url, page, isSwUser, section });
  };

  const getParams = () => {
    if (page === Page.Meeting) {
      const matchedCompanies = companiesQuery.data?.filter((company) => company.domain !== userDomain).length;
      return {
        total_companies_in_meeting: collectedMeetingDomains.filter((domain) => domain !== userDomain).length,
        matched_companies_in_meeting: matchedCompanies || 0,
      };
    }

    return;
  };

  const trackToolsAndCrmPageView = () => {
    const eventName = companyDomain;

    if (
      (prevUrl !== url && prevUrl && new URL(prevUrl).hostname === urlObject?.hostname) ||
      (lastLoggedToolsDomain.current === eventName && companyTabName === prevCompanyTabName)
    ) {
      return;
    }

    lastLoggedToolsDomain.current = eventName;
    TrackingPageViewService.trackPageView({ config, url, eventName, page, isSwUser, params: getParams() });
  };

  useEffect(() => {
    if (!isOpen || !url || isWithinCrm || isWithinTools) {
      return;
    }

    trackPageView();
  }, [page, isOpen, companyTabName, url, isSAMOnboardingCompleted]);

  useEffect(() => {
    if (!isOpen || !url || (!isWithinCrm && !isWithinTools)) {
      return;
    }

    if (companiesQuery.isSuccess || companyQuery.isSuccess) {
      trackToolsAndCrmPageView();
    }
  }, [
    page,
    isOpen,
    companyTabName,
    url,
    companyDomain,
    companiesQuery.isSuccess,
    companyQuery.isSuccess,
    collectedMeeting,
  ]);
};
